import React, { useEffect, useMemo } from "react";
import { Spinner, Typography } from "@optimizely/axiom";

import { DuplicateRole } from "./DuplicateRole";
import { SelectRoleProjects } from "./SelectRoleProjects";
import { SelectRoleAttributes } from "./SelectRoleAttributes";

import { Role } from "../../../domain/Role";
import { RoleAttribute } from "../../../domain/RoleAttribute.interface";
import { Flags } from "../../../feature-flags/flags";

import { useAttributes } from "../../../hooks/useAttributes/useAttributes";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { ATTRIBUTE_KEYS } from "../../../constants/constants";

type RoleAttributeSelectionsProps = {
    hideDuplicateRole?: boolean;
    onChange: ({
        preMappedValues,
        id,
        key,
        values
    }: {
        id?: string;
        key?: string;
        preMappedValues?: boolean;
        values: string[] | RoleAttribute[];
    }) => void;
    productId?: string;
    instanceId?: string;
    roles: Role[];
    selectedAttributes?: RoleAttribute[];
    rolePermissionValue?: string;
};

const AttributeSelectionHeading = () => (
    <div className="push-triple--top">
        <Typography type="header4">
            <span style={{ fontWeight: 300 }}>
                Select Attributes <span aria-label="(required)" className="oui-label--required" />
            </span>
        </Typography>
    </div>
);

export const RoleAttributeSelections = ({
    hideDuplicateRole,
    onChange,
    productId,
    instanceId,
    roles,
    selectedAttributes = [],
    rolePermissionValue = ""
}: RoleAttributeSelectionsProps) => {
    const { enabled: enableCmpRoleEnhancement } = useFeatureFlag(Flags.ENABLE_CMP_ROLE_ENHANCEMENT);

    const isExp = productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;
    const isCmpSelected = productId === process.env.REACT_APP_CMP_PRODUCT_ID;

    const { attributes, isLoadingInitialData: isLoading = false } = useAttributes({
        isActive: true,
        productId
    });

    // Track CMP team attribute for enhancement feature
    const cmpTeamAttribute = useMemo(
        () =>
            attributes?.find(
                (attr) => attr.productId === process.env.REACT_APP_CMP_PRODUCT_ID && attr.key === ATTRIBUTE_KEYS.TEAM
            ) ?? null,
        [attributes]
    );
    const selectableAttributes = attributes ?? [];

    const cmpTeamAttributeEnabled =
        !!cmpTeamAttribute && selectedAttributes.some((attr) => attr.id === cmpTeamAttribute.id);

    useEffect(() => {
        if (enableCmpRoleEnhancement && !!cmpTeamAttribute) {
            window.dispatchEvent(
                new CustomEvent("cmp-role", {
                    detail: {
                        cmpTeamAttribute,
                        cmpTeamAttributeEnabled
                    }
                })
            );
        }
    }, [enableCmpRoleEnhancement, cmpTeamAttribute, cmpTeamAttributeEnabled]);

    const productRoles = roles.filter((r) => r.productId === productId);

    const handleDuplicateSelectionChange = ({ role }: { role?: Role }) => {
        onChange({ preMappedValues: true, values: role ? role.attributes : [] });
    };

    if (isLoading) {
        return (
            <div className="push-triple--top flex justify-content-center">
                <Spinner />
            </div>
        );
    }

    if (isCmpSelected && cmpTeamAttributeEnabled) {
        return (
            <>
                <AttributeSelectionHeading />
                <div className="push-quad--bottom">
                    <Typography type="body" className="push--bottom">
                        Team Roles do not support permissions.
                    </Typography>
                </div>
            </>
        );
    }

    return (
        <>
            {!hideDuplicateRole && !!roles.length && (
                <div className="push-triple--top">
                    <DuplicateRole roles={productRoles} onChange={handleDuplicateSelectionChange} />
                </div>
            )}
            <AttributeSelectionHeading />
            <div className="push--bottom">
                <Typography type="body" className="push--bottom">
                    Check all attributes to be assigned to this role.
                </Typography>
            </div>

            {isExp ? (
                <SelectRoleProjects
                    onChange={onChange}
                    productId={productId}
                    instanceId={instanceId}
                    selectedAttributes={selectedAttributes}
                    rolePermissionValue={rolePermissionValue}
                />
            ) : (
                <SelectRoleAttributes
                    loading={isLoading}
                    attributes={selectableAttributes}
                    onChange={onChange}
                    productId={productId}
                    selectedAttributes={selectedAttributes}
                />
            )}
        </>
    );
};

RoleAttributeSelections.displayName = "RoleAttributeSelections";
